import Customer from './customer';
import JSDB, {NKTypeConvertor} from '../fromKotlin/nk';
import {ExtendedDocument, notesArePrinted, ProductNote} from './DocumentsTypes/Document';
import {ICustomer} from '../cdmanagment/src/types/customerStore-types';
import {dateObjectToString, roundDecimals, StringToDateTimeObject, StringToIsrael} from '../fromKotlin/nkutils';
import Agent from './agent';
import {secondUnitIndex, thirdUnitIndex} from './product_delivery';
import {isVariableConversionRate} from '../utils/cofcFeatures';

export interface OrderData {
    checked?: boolean;
    orderId?: string;
    date?: string;
    notes?: string;
    category?: string;
    status?: string;
    id?: string;
    externalId?: string;
    customer?: string;
    agent?: string;
    driverName?: string;
    car?: string;
    driverId?: number;
    collectorName?: string;
    collectorId?: number;
    mName?: number;
    dvs?: ProductNote[];
    position?: number;
    min_order?: number;
    min_order_sum?: number;
    tn?: boolean;
    driver_position?: number;
    collector_position?: number;
    readonly: boolean;
    all_dvs_printed?: boolean;
    hasBeenEdited?: boolean;
    break_row?: boolean;
    city?: string
    c_category?: string
    cp_category?: string
    notes2?: string
    notes3?: string
    notes4?: string
    notes5?: string
    notes6?: string
    notes7?: string
    notes8?: string
    notes9?: string
    notes10?: string
    last_created?: string
    last_updated?: string
    ref_id?: string
    collectorData?: string
    /** Supply date (תאריך אספקה), distinct from the order document date. */
    delivery_date?: string
    /** Forced (static_data) business name overriding the client on the printed order. */
    forced_business_name?: string
    /** Forced (static_data) address overriding the client on the printed order. */
    forced_address?: string
    /** Set when forced static fields / delivery date were edited in the grid, so the save sends them. */
    static_edit?: boolean
    delivery_date_edit?: boolean
    /** Temporary/forced driver name (נהג זמני) coming from OrderNote.agent_id. */
    orderDriver?: string
    /**
     * When true, [driver_position] holds a per-order forced route position (OrderNote.force_position)
     * instead of the client daily position. The original daily position is kept in [daily_position].
     */
    force_position_on?: boolean
    /** Underlying clients_daily position, preserved while a forced position overrides the display. */
    daily_position?: number
    nkObject?: any
    /** Optimistic lock copied from OrderNote.row_version. 0 = not loaded / old bundle. */
    row_version?: number
    /** True when the green drawer wrote price_/discount_/use_price_ for this row. */
    pricesDirty?: boolean
    /** Dirty local edits while the server has a newer row_version. */
    syncConflict?: boolean
}

/** True only when נהג זמני is a real non-empty name. Empty string is never a temp driver. */
export const hasTempOrderDriver = (d?: { orderDriver?: string | null } | null): boolean =>
    !!(d?.orderDriver != null && String(d.orderDriver).trim() !== '');

export const orderRowVersion = (d?: { row_version?: number, nkObject?: any } | null): number => {
    const n = Number(d?.row_version ?? d?.nkObject?.rowVersion?.() ?? d?.nkObject?.row_version ?? 0);
    return Number.isFinite(n) ? n : 0;
};

export const createOrderDataEasy = (d: OrderData, orderId?: string, readOnly?: boolean) => {
    const curData = createOrderData({
        orderId: orderId ?? d.orderId,
        id: d.id!,
        customer: d.customer,
        agent: d.agent,
        driverName: d.driverName,
        position: d.position,
        collectorName: d.collectorName,
        driver_position: d.driver_position,
        collector_position: d.collector_position,
        driver_id: d.driverId,
        collector_id: d.collectorId,
        mName: d.mName,
        min_order: d.min_order,
        min_order_sum: d.min_order_sum,
        tn: d.tn,
        readonly: readOnly ?? false,
        city: d.city,
        c_category: d.c_category,
        car: d.car,
        date: d.date

    });

    curData.externalId = d.externalId
    curData.cp_category = d.cp_category
    // Per-order temporary details (שם עסק מאולץ, כתובת מאולצת, נהג זמני, תאריך אספקה, סדר מאולץ).
    curData.forced_business_name = d.forced_business_name
    curData.forced_address = d.forced_address
    curData.delivery_date = d.delivery_date
    curData.orderDriver = d.orderDriver
    curData.force_position_on = d.force_position_on
    curData.daily_position = d.daily_position
    curData.row_version = d.row_version
    curData.pricesDirty = d.pricesDirty
    curData.syncConflict = d.syncConflict
    return curData
}

/** Drop כתובת/שם עסק מאולץ copied from another order. A note that really has them sets them again. */
export const clearCopiedForcedDetails = (row: OrderData) => {
    row.forced_address = '';
    row.forced_business_name = '';
    return row;
}

/** Notes that lock an order row (teal / readonly) in the orders grid. */
export const ORDER_LOCKING_NOTE_TYPES = new Set([
    'delivery_certificate', 'invoice', 'tax_invoice', 'refund', 'visit_note'
]);

export const isCanceledOrderDocument = (doc?: { type?: string; status?: string; orderStatus?: string } | null): boolean =>
    !!doc && doc.type === 'order' && (doc.status === 'canceled' || doc.orderStatus === 'מבוטלת');

/** Empty client row after an order is canceled — same shape as "מחק הזמנה". */
export const resetOrderRowToEmpty = (d: OrderData): OrderData => {
    const curData = createOrderData({
        orderId: '',
        id: d.id!,
        customer: d.customer,
        agent: d.agent,
        driverName: d.driverName,
        collectorName: d.collectorName,
        driver_position: d.driver_position,
        position: d.position,
        collector_position: d.collector_position,
        driver_id: d.driverId,
        collector_id: d.collectorId,
        mName: d.mName,
        min_order: d.min_order,
        min_order_sum: d.min_order_sum,
        tn: d.tn,
        readonly: false,
        city: d.city,
        c_category: d.c_category,
        cp_category: d.cp_category,
        car: d.car,
        date: d.date,
    });
    curData.externalId = d.externalId;
    curData.hasBeenEdited = false;
    return curData;
};

const linkedDeliveryIds = (doc?: ExtendedDocument | null): number[] =>
    (doc?.deliveryCertificatesIds ?? []).map((id) => Number(id)).filter((id) => !Number.isNaN(id) && id > 0);

/**
 * Apply a canceled document (order / invoice / DV) onto one orders-grid row.
 * Returns a new row, or null if this client/order is unrelated.
 *
 * Israel tax-invoice cancel issues a new credit and leaves the original invoice in place —
 * keep the original on the row (teal / readonly) and attach the credit when it is still active.
 */
export const applyCanceledDocumentToOrderRow = (
    d: OrderData,
    original: ExtendedDocument,
    result?: ExtendedDocument | null
): OrderData | null => {
    const origId = Number(original?.id);
    const linkedDvs = new Set(linkedDeliveryIds(original));
    const dvsHas = !!d.dvs?.some((dv) => Number(dv.id) === origId || linkedDvs.has(Number(dv.id)));
    const linkedOrderId = original.orderId != null && Number(original.orderId) !== -1
        ? String(original.orderId)
        : undefined;
    const orderMatch = original.type === 'order'
        ? String(d.orderId) === String(original.id)
        : !!(linkedOrderId && String(d.orderId) === linkedOrderId);
    const clientLockedMatch = String(original.entId) === String(d.id)
        && !!d.orderId && d.orderId !== ''
        && (d.readonly || (d.dvs?.length ?? 0) > 0);

    if (original.type === 'order') {
        if (String(d.orderId) !== String(original.id)
            && !(result?.type === 'order' && String(d.orderId) === String(result.id))) {
            return null;
        }
        return resetOrderRowToEmpty(d);
    }

    if (!dvsHas && !orderMatch && !clientLockedMatch) return null;

    const issuedNewDoc = !!(result
        && (Number(result.id) !== origId || result.type !== original.type)
        && ORDER_LOCKING_NOTE_TYPES.has(result.type));

    let curDvs = issuedNewDoc
        ? [...(d.dvs ?? [])]
        : (d.dvs ?? []).filter((dv) => Number(dv.id) !== origId);

    if (issuedNewDoc && result) {
        if (!curDvs.some((dv) => Number(dv.id) === origId) && ORDER_LOCKING_NOTE_TYPES.has(original.type)) {
            curDvs = [...curDvs, {...original, status: original.status === 'canceled' ? 'closed' : original.status} as ProductNote];
        }
        if (result.status !== 'canceled' && !curDvs.some((dv) => Number(dv.id) === Number(result.id))) {
            curDvs = [...curDvs, result as ProductNote];
        }
    }

    const locked = curDvs.length > 0 || issuedNewDoc;
    return {
        ...d,
        dvs: curDvs,
        readonly: locked,
        all_dvs_printed: notesArePrinted(curDvs) || (curDvs.length === 0 && locked),
        hasBeenEdited: false,
    };
};
export const createOrderData: ({
                                   orderId,
                                   id,
                                   customer,
                                   agent,
                                   driverName,
                                   collectorName,
                                   mName,
                                   customerObj,
                                   position,
                                   driver_position,
                                   collector_position,
                                   checked,
                                   dvs,
                                   readonly,
                                   collector_id,

                                   driver_id,
                                   min_order,
                                   tn,
                                   city,

                                   c_category,
                                   car,
                                    date,
                                   min_order_sum,
    cp_category,

                               }: {
    orderId?: string;
    id?: string;
    customer?: string;
    agent?: string;
    driverName?: string;
    collectorName?: string;
    mName?: number;
    customerObj?: Customer;
    position?: number
    driver_position?: number
    collector_position?: number
    checked?: boolean
    dvs?: ProductNote[],
    readonly: boolean,
    collector_id?: number,
    driver_id?: number,
    min_order?: number
    tn?: boolean,
    city?: string
    c_category?: string,
    car?: string,
    date?:string,
    min_order_sum?:number,
    cp_category?:string,
}) => OrderData = ({
                       orderId = '',
                       id = '',
                       customer = '',
                       agent = '',
                       driverName = '',
                       collectorName = '',
                       mName = undefined,
                       customerObj = undefined,
                       position = 0,
                       driver_position = 0,
                       collector_position = 0,
                       checked = false,
                       dvs = undefined,
                       readonly = false,

                       collector_id = -1,
                       driver_id = -1,
                       min_order = undefined,
                       tn = undefined,
                       city = undefined,
                       c_category = undefined,
                       car = undefined,
                        date=undefined,
                min_order_sum=0,
    cp_category=undefined


                   }) => ({
    orderId,
    id,
    customer,
    agent,
    driverName,
    collectorName,
    mName,
    customerObj,
    position,
    driver_position,
    collector_position,
    checked,
    dvs,
    readonly,
    collector_id,
    driver_id,
    min_order,
    tn, city,
    c_category,
    car,date,
    min_order_sum,
    cp_category

});

/**
 * Copy per-order temporary overrides from a source note onto a grid row.
 * Used both when loading an existing order and when duplicating (writeOrder=false),
 * so שם עסק מאולץ / כתובת מאולצת / תאריך אספקה / סדר מאולץ survive שכפול.
 */
const applyOrderForcedDetailsFromNote = (curData: OrderData, order: ProductNote) => {
    const nk = order?.nkObject;
    const dd = nk?.getDeliveryDate?.() ?? (order as any).delivery_date;
    if (dd) {
        const s = dd instanceof Date ? dateObjectToString(dd) : String(dd);
        const day = s.split(' ')[0];
        if (day && day !== 'undefined' && day !== 'null') {
            curData.delivery_date = day;
        }
    }
    const sd = nk?.getStaticData?.();
    if (sd && ((sd.business_name ?? '') !== '' || (sd.address ?? '') !== '')) {
        curData.forced_business_name = sd.business_name ?? '';
        curData.forced_address = sd.address ?? '';
    } else {
        const details = (order as any).staticData;
        if (details) {
            const bn = details.companyName ?? details.business_name ?? '';
            const addr = String(details.address ?? '').trim()
                || [details.city, details.street, details.streetNumber]
                    .map((x: string) => (x ?? '').trim())
                    .filter(Boolean)
                    .join(',');
            if (bn || addr) {
                curData.forced_business_name = bn;
                curData.forced_address = addr;
            }
        }
    }
    // Forced route position (OrderNote.force_position): show it instead of the daily position,
    // keeping the underlying daily position aside so daily saves don't get corrupted.
    // NOTE: must read via the annotated hasForcedPosition()/getEffectivePosition() methods — the
    // raw `force_position` property is name-mangled in the compiled Kotlin/JS bundle (no @myName
    // on the property itself), so `order?.nkObject?.force_position` is always undefined here.
    if (nk?.hasForcedPosition?.()) {
        const fp = nk?.getEffectivePosition?.();
        if (fp !== undefined && fp !== null && fp !== -1) {
            if (curData.daily_position == undefined) {
                curData.daily_position = curData.driver_position;
            }
            curData.driver_position = fp;
            curData.force_position_on = true;
        }
    }
};

/** נהג זמני from OrderNote.agent_id. Empty / blank-named agents must not lock the נהג column. */
const applyOrderTempDriverFromNote = (curData: OrderData, order: ProductNote) => {
    const tempDriverName = order.agent_id && order.agent_id != -1
        ? JSDB().getAgentBy(order.agent_id, null, false, false, false, true)?.first?.user_name
        : undefined;
    curData.orderDriver = (tempDriverName != null && String(tempDriverName).trim() !== '')
        ? String(tempDriverName)
        : undefined;
};

export const writeOrdersToOD = (curData: OrderData, order: ProductNote, writeOrder: boolean = true, active_products?, noComments?, date?: string, withPrices: boolean = true) => {
    if (writeOrder) {
        curData.orderId = order.id.toString();
        curData.status = order.orderStatus;
        curData.ref_id = order.ref_id;
        curData.nkObject = order.nkObject
        curData.date= dateObjectToString(order.date)
        curData.row_version = orderRowVersion({ nkObject: order.nkObject, row_version: (order as any).row_version })
        curData.pricesDirty = false
        curData.syncConflict = false
    }
    applyOrderForcedDetailsFromNote(curData, order);
    curData.notes = order.comment;
    curData.notes2 = order.notes2;
    curData.notes3 = order.notes3;
    curData.notes4 = order.notes4;
    curData.notes5 = order.notes5;
    curData.notes6 = order.notes6;
    curData.notes7 = order.notes7;
    curData.notes8 = order.notes8;
    curData.notes9 = order.notes9;
    curData.notes10 = order.notes10;
    curData.category = order.category;
    applyOrderTempDriverFromNote(curData, order);
    if (!noComments) {

        curData.last_updated = order?.nkObject?.action_time ? StringToDateTimeObject(order?.nkObject?.action_time) : undefined
        curData.last_created = order?.nkObject?.date_issued ? StringToDateTimeObject(order?.nkObject?.date_issued) : undefined;

        curData.collectorData = order?.nkObject?.getKartonString()
        const karLst = order?.nkObject?.getKartonLst()
        if (karLst && karLst.size > 0) {
            karLst.toArray().forEach((k) => {
                curData[`c_${k.id}`] = k.box
            })
            if (JSDB().getCompany() == 'misrael') {
                const x = JSDB().getKartonAutoCollection(order.nkObject.delivery_info)
                if (x.size > 0) {
                    x.toArray().forEach((k) => {
                        curData[`c_${k.id}`] = k.box
                    })
                }
            }
        }

    }
    const client = JSDB().getClient(order.nkObject.ent_id).first
    const pds = []
    order.products.forEach((op) => {
        if (!active_products || (active_products.has(op.id)) && (!date || client.getPrice(op.id).get(date).first != 0)) {
            pds.push(op.nkObject)
            if (op.available_units.length >= 2) {
                let value = 0;
                let unit = 0;
                // resolve index dynamically: available_units may be [first, second], [first, third]
                // (no second unit), or [first, second, third] - a bare length check can't tell them apart
                const hasSecond = secondUnitIndex(op) >= 0;
                const hasThird = thirdUnitIndex(op) >= 0;
                if (op.quantity > 0) {
                    value = op.quantity;
                } else if (hasSecond && op.quantitySecond > 0) {
                    unit = 1;
                    value = op.quantitySecond;
                }
                // order-only third unit: unit index 2, amount kept as-is (no conversion)
                else if (hasThird && (op.quantityThird ?? 0) > 0) {
                    unit = 2;
                    value = op.quantityThird ?? 0;
                }
                curData[`u_${op.id}`] = unit;
                if (isVariableConversionRate()) {
                    const catalog = JSDB().getClientProduct(op.id).first?.getOrderProduct()?.conversion_rate ?? 0;
                    const line = Number(op.conversion_ratio) || 0;
                    curData[`cr_${op.id}`] = line > 0 ? line : catalog;
                }


                curData[`p_${op.id}`] = (curData[`p_${op.id}`] ?? 0) + (value ?? 0);
            } else {
                curData[`p_${op.id}`] = (curData[`p_${op.id}`] ?? 0) + (op.quantity ?? 0);
            }
            if (withPrices && op.nkObject?.price != null) {
                curData[`price_${op.id}`] = op.nkObject.price;
                curData[`discount_${op.id}`] = op.nkObject.discount;
                curData[`use_price_${op.id}`] = op.nkObject.use_price;
            }

            if (op.isLocked && writeOrder)
                curData[`lock_${op.id}`] = 1;
            curData[`np_${op.id}`] = op.nkObject?.notes;
            // Bundle for Excel export: `aap_{sourceId}` = "id1_id2_..." (see Excel-export-modal).
            // Prefer line `notes2`; else catalog product `notes2` on the client product.
            let bundleRep = (op.notes2 ?? '').toString().trim();
    
            if (!bundleRep) {
                const catalogP: any = JSDB().getClientProduct(op.id).first;
                const n2 = catalogP?.notes2;
                const n3 = catalogP?.notes3;

                if (n3=="bundle" && n2 != null && String(n2).trim() !== '') {
                    bundleRep = String(n2).trim();
                }
            }
            if (bundleRep) {
                curData[`aap_${op.id}`] = bundleRep;
            }
            
            if ((JSDB().getCompany() == 'pommeranz'))
                curData[`rrp_${op.id}`] = op.quantity + op.quantitySecond * op.conversion_ratio;
          
        }
    });

    curData.karton = roundDecimals(JSDB().kartonEstimate(NKTypeConvertor().toKotlinList(pds)));
    order.history?.forEach((op) => {
        if (!active_products || (active_products.has(op.id)) && (!date || client.getPrice(op.id).get(date).first != 0)) {
            if (op.available_units.length >= 2) {
                let value = 0;
                let unit = 0;
                const hasSecond = secondUnitIndex(op) >= 0;
                const hasThird = thirdUnitIndex(op) >= 0;
                if (op.quantity > 0) {
                    value = op.quantity;
                } else if (hasSecond && op.quantitySecond > 0) {
                    unit = 1;
                    value = op.quantitySecond;
                } else if (hasThird && (op.quantityThird ?? 0) > 0) {
                    unit = 2;
                    value = op.quantityThird ?? 0;
                }
                curData[`hu_${op.id}`] = unit;
                curData[`hp_${op.id}`] = (curData[`hp_${op.id}`] ?? 0) + (value ?? 0);
            } else {
                curData[`hp_${op.id}`] = (curData[`hp_${op.id}`] ?? 0) + (op.quantity ?? 0);
            }
        }
    });

};
export const orderKeyLite = (curData: OrderData) => {
    return `${curData.id}_${curData.orderId}`
};
export const splitOrderKeyLite = (key) => {
    const k = key.split('_');
    return `${key[0]}_${k[1] ?? ''}`
};
export const orderKey = (curData: OrderData) => {
    return orderKeyRef(curData)

};
export const orderKeyRef = (curData: OrderData) => {
    return `${curData.id}_${curData.orderId}_${curData.ref_id ?? ''}`;

};
export const extractIdFromKey = (key: string) => {
    return key.split('_')[0];
}
export const writeDVsToOD = (curData: OrderData, dv: ProductNote[]) => {
    curData.dvs = dv;
    curData.readonly = true;
    curData.all_dvs_printed = notesArePrinted(dv);
    dv.forEach((curNote) => curNote.products?.forEach((op) => {
        const mainQ = op.quantity + op.quantitySecond * op.conversion_ratio;
        if (curData[`n_${op.id}`])
            curData[`n_${op.id}`] += mainQ;
        else
            curData[`n_${op.id}`] = mainQ;
        if (curData[`sn_${op.id}`])
            curData[`sn_${op.id}`] += op.quantitySecond;
        else
            curData[`sn_${op.id}`] = op.quantitySecond;
    }));
};
export const getKartonNum = (d: OrderData): number => {
    return roundDecimals(JSDB().kartonEstimate(deserializeOrderDataToOrder(d)));
};
export const getSumProducts = (d: OrderData): number => {
    let s = 0
    Object.keys(d).forEach((p) => {
        if (p.startsWith('p_') && d[p]) {
            s += d[p]
        }
    });
    return roundDecimals(s);
};
export const deserializeOrderDataToClientDaily = (d: OrderData, date: string, day: number): any =>
    // When the shown position is a per-order forced one, keep the client daily position untouched.
    JSDB().createDailyData(date, Number(d.id), d.driverName ?? '', d.collectorName ?? '', d.force_position_on ? (d.daily_position ?? d.driver_position) : d.driver_position, d.collector_position, d.mName ?? 0, day, d.car ?? "");
export const deserializeCustDataToClientDaily = (d: ICustomer, position: number, col_position: number, date: string): any => JSDB().createDailyData(date, Number(d.id), d.driverId ?? '', d.collectorId ?? '', position, col_position, d.crateId ?? 0);
export const deserializeOrderDataToOrder = (d: OrderData): any => {
    const ret: any = [];
    const priceLines = (!d.pricesDirty && d.nkObject?.delivery_info)
        ? (typeof d.nkObject.delivery_info.toArray === 'function' ? d.nkObject.delivery_info.toArray() : [])
        : [];
    Object.keys(d).forEach((p) => {
        if (p.startsWith('p_') && (Number(d[p] || d.keep))) {

            const id = Number(p.split('_')[1]);
            const pd = JSDB().createProductDelivery(id);
            if ((d as any).returnsOnly) {
                // Returns-only order/note: the amount represents returned goods.
                pd.returns = d[p]
            } else if (d[`u_${id}`] == 2) {
                // order-only third unit (no conversion): store amount + label unit, never charged
                pd.third_amount = d[p]
                pd.third_unit = JSDB().getClientProduct(id).first?.getOrderProduct()?.third_unit ?? 0
            } else if (d[`u_${id}`]) {
                pd.wrapped_amount = d[p]
                const catalog = JSDB().getClientProduct(id).first?.getOrderProduct()?.conversion_rate ?? 0
                pd.conversion_ratio = isVariableConversionRate() && d[`cr_${id}`] != null
                    ? (Number(d[`cr_${id}`]) || 0)
                    : catalog
            } else {
                pd.value = d[p]
            }
            const srcLine = priceLines.find((line: any) => Number(line.productId ?? line.product_id) === id);
            if (!d.pricesDirty && srcLine) {
                if (srcLine.price != null) pd.price = srcLine.price;
                if (srcLine.discount != null) pd.discount = srcLine.discount;
                if (srcLine.use_price != null) pd.use_price = srcLine.use_price;
            } else {
                if (d[`price_${id}`] != null) {
                    pd.price = d[`price_${id}`]
                }
                if (d[`use_price_${id}`] != null) {
                    pd.use_price = d[`use_price_${id}`]
                }
                if (d[`discount_${id}`] != null) {
                    pd.discount = d[`discount_${id}`]
                }
            }
            if (d[`lock_${id}`]) {
                pd.o_locked = 1
            }
            if (d[`np_${id}`]) {

                pd.notes = d[`np_${id}`].replace("\"", "").replace("\'", "").replace("\n", "")
            }
            ret.push(pd);
        }
    });
    return NKTypeConvertor().toKotlinList(ret);
};
export const deserializeOrderDataToDV = (d: OrderData): any => {
    const ret: any = [];
    Object.keys(d).forEach((p) => {
        if (p.startsWith('n_') && d[p] !== 0) {
            const id = Number(p.split('_')[1]);
            const pd = JSDB().createProductDelivery(id);
            if (d[`sn_${id}`]) {
                pd.wrapped_amount = d[`sn_${id}`] ?? 0
                const catalog = JSDB().getClientProduct(id).first?.getOrderProduct()?.conversion_rate ?? 0
                pd.conversion_ratio = isVariableConversionRate() && d[`cr_${id}`] != null
                    ? (Number(d[`cr_${id}`]) || 0)
                    : catalog
            } else {
                pd.value = d[p] ?? 0
            }
            ret.push(pd);
        }
    });
    return NKTypeConvertor().toKotlinList(ret);
};
export const deserializeDataStruct = (d: any[]): any => {
    const ret: any = [];
    d.forEach((p) => {
        if (p.amount_dv > 0 || p.amount_second_dv > 0) {
            const pd = JSDB().createProductDelivery(p.p_id);
            pd.value = p.amount_dv;
            if (p.price_change)
                pd.price = p.price;
            pd.wrapped_amount = p.amount_second_dv ?? 0;
            pd.conversion_ratio = p.conversion_rate ?? 0;
            if (p.notes) {
                pd.notes = (p.notes ?? '').replace("\"", "").replace("\'", "").replace("\n", "");

            }
            if (p.isLocked)
                pd.o_locked = 1;

            ret.push(pd);
        }
    });

    return NKTypeConvertor().toKotlinList(ret);
};
export const deserializeDataStructOld = (d: any[]): any => {
    const ret: any = [];
    d.forEach((p) => {
        if (p.amount_dv > 0 || p.amount_second_dv > 0) {
            const pd = JSDB().createProductDelivery(p.id);
            pd.value = p.amount_dv;
            pd.price = p.price;
            pd.wrapped_amount = p.amount_second_dv ?? 0;
            pd.conversion_ratio = p.conversion_rate ?? 0;
            ret.push(pd);
        }
    });
    return NKTypeConvertor().toKotlinList(ret);
};
export const spreadAmountRoundRobin = (d: OrderData[], col, amount, activeOnly: boolean = false): any[] => {
    // assuming one has already filtered the data, and d is too be algorithmed
    const sortedData = d.map((v) => v).sort((a, b) => (b[col] ?? 0) - (a[col] ?? 0));
    const change: any[] = sortedData.map((m) => ({
        id: m.id,
        value: m[col] ?? 0
    }));
    roundRobinFull(change, amount, activeOnly, 6);
    return change;

};

interface result {
    value: any[];
    missing: number;
}

export const roundRobinFull = (change: any[], toSpread: number, activeOnly: boolean = false, tries: number = 3): result => {
    // assuming one has already filtered the data, and d is too be algorithmed
    let missed = toSpread;
    let chager = activeOnly ? change.filter((d) => d.value && d.value != 0) : change;
    while (tries > 0 && missed != 0) {
        const result = roundRoundRobin(chager, missed);
        missed = result.missing;
        chager = result.value;
        tries -= 1;
    }
    return {value: change, missing: missed};
};
export const roundRoundRobin = (change: any[], toSpread: number): result => {
    const chager = toSpread >= 0 ? change : change.filter((d) => d.value && d.value > 0);
    const size = chager.length;
    const spreadEach = toSpread > 0 ? Math.floor(toSpread / size) : Math.ceil(toSpread / size);
    let extra = toSpread - spreadEach * size;
    chager.forEach((sd) => {
        if (toSpread >= 0) {
            if (sd.value)
                sd.value += spreadEach;
            else
                sd.value = spreadEach;
            if (extra > 0) {
                sd.value += 1;
                extra -= 1;
            }
        } else {
            if (sd.value) {
                if (sd.value + spreadEach < 0) {
                    extra += (sd.value + spreadEach);
                    sd.value = 0;
                } else {
                    sd.value += spreadEach;
                }
                if (extra < 0 && sd.value > 0) {
                    sd.value -= 1;
                    extra += 1;
                }
            }
        }
    });
    return {value: change, missing: extra};
};
export const spreadPercentage = (change: any[], have: number, toReach: number): result => {
    // assuming one has already filtered the data, and d is too be algorithmed
    let cur = 0;
    change.forEach((sd) => {
        sd.value = Math.floor((sd.value / have) * toReach);
        cur += sd.value;
    });


    return {value: change, missing: toReach - cur};

};
export const spreadAmountStronger = (d: OrderData[], col, amount): OrderData[] => {
    // assuming one has already filtered the data, and d is too be algorithmed
    const sortedData = d.map((v) => v).sort((a, b) => (b[col] ?? 0) - (a[col] ?? 0));
    const sumFunc = sortedData.reduce((sum, x) => sum + (x[col] ?? 0), 0) ?? 0;
    let change: any[] = sortedData.map((m) => ({
        id: m.id,
        value: m[col] ?? 0
    }));
    const result = spreadPercentage(change, sumFunc, amount);
    if (result.missing > 0) {
        const withValue = change.filter((sd) => sd.value > 0);
        roundRobinFull(withValue, result.missing, true, 2);

    }


    return change;

};





